import _ from 'lodash';
import { createContext } from 'react';
import { history } from 'umi';

import { bindServices } from '@/services/api';
import { updateFavorite } from './service';

const api = bindServices('entities');
import request from '@/utils/infrastructure/communication/request';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { filtersConfig } from './ManageEntities/utils';
import { groupsFiltersConfig } from './manage-groups/utils';

export const DynamicEntityContext = createContext({});

const { DynamicEntityOperation } = CoreEnums;
const meta = { page: 1, limit: 5, count: 0, totalPages: 0 };

const initialState = {
  data: [],
  groups: [],
  premium: [],
  favorites: [],
  groupsPagination: meta,
  premiumPagination: meta,
  pagination: meta,
  favoritesPagination: meta,
  orderBy: '',
  currentEntity: null,
  searchCriteria: '',
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
};

const Model = {
  namespace: 'entities',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/groups',
          ...CoreQueries.ManageEntities.Groups,
          ...payload,
        });
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    // Main management view (`/entities` sidebar menu) → `/entity/manage`.
    // Per the UX guide `/manage` is reserved for top-level management
    // screens accessed from the left sidebar; every other context
    // (selectors, dependencies, pickers, embedded dialogs) uses
    // `/entity/allowed`. `canManage` gates configuration UI;
    // `isFavorite` drives the star icon on each card.
    *fetchList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/manage',
          ...CoreQueries.ManageEntities.Entities,
          select: `${CoreQueries.ManageEntities.Entities.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryEntities',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    *fetchPremiumList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/manage',
          ...CoreQueries.ManageEntities.PremiumEntities,
          select: `${CoreQueries.ManageEntities.PremiumEntities.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryPremiumEntities',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Favorites-first load via the dedicated `/entity/favorites`
    // endpoint. `canManage` is required for the row-command kebab and
    // `isFavorite` keeps the star icon filled (the endpoint returns
    // only favorites but the flag isn't auto-included in the select).
    *fetchFavorites({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/favorites',
          ...CoreQueries.ManageEntities.Favorites,
          select: `${CoreQueries.ManageEntities.Favorites.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryFavorites',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Star toggle — mirrors the `/processes` contract:
    //   1. `querySetFavorite` flips the flag locally (optimistic).
    //   2. `markFavoritesChanged` flags the slice so the next time the
    //      user lands on the favorites tab, `onTabChange` refreshes it.
    // No re-fetch happens here: re-reading `/entity/manage` or
    // `/entity/favorites` right after the POST/DELETE clobbers the
    // optimistic update with potentially-stale replica data.
    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });
        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *saveEntity({ payload }, { call, put }) {
      try {
        const result = yield call(api.create, { route: 'dynamicEntity', ...payload });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'querySaveEntity' });

        const newId = result?.id || result?.data?.id;
        if (newId) {
          history.push(`/entities/${newId}/builder`);
        }
      } catch (err) {
        if (err?.message) {
          const { message, validationErrors } = err;
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message },
            validationErrors,
          });
        }
      }
    },

    *entityGroup({ payload }, { call, put }) {
      try {
        yield call(api.create, { route: 'dynamicEntityGroup', data: payload });
        yield put({
          type: 'queryEntityGroup',
          payload: DynamicEntityOperation.Add,
        });
      } catch (err) {
        if (err?.message) {
          const { message, validationErrors } = err;
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message },
            validationErrors,
          });
        }
      }
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    // Only refresh the favorites tab when the slice has been touched
    // since the last fetch (`favoritesChanged`). Same contract as
    // `/processes`: a clean re-entry keeps the cached list intact.
    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.entities);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(request.post, `/dynamicEntity/activate/${payload.id}/${payload.isActive}`, {});
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    // Generic delete used by the new Launchpad surface. The legacy admin
    // page routes deletions through `configEntity/deleteEntity`, but that
    // model is only registered while `/entities/:id` is mounted. Keeping
    // a small saga here lets the Launchpad EntityDeleteHost reuse the
    // already-loaded `entities` namespace without forcing the heavier
    // configuration model to load on the home page.
    *deleteItem({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `dynamicEntity/${payload.id}`,
          method: 'delete',
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryDeleteItem', payload: { id: payload.id } });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationGlobal',
            payload: { visible: true, type: 'error' },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *hideAllForms(o, { put }) {
      yield put({ type: 'hideForms' });
    },

    *cleanup(o, { put }) {
      yield put({ type: 'queryCleanup' });
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        executedCommand: null,
        viewGroupsExecutedCommand: null,
        groupsPagination: {
          ...state.groupsPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryEntities(state, { payload }) {
      return {
        ...state,
        data: payload?.items || [],
        executedCommand: null,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryPremiumEntities(state, { payload }) {
      return {
        ...state,
        premium: payload?.items || [],
        executedCommand: null,
        premiumPagination: {
          ...state.premiumPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload?.items || [],
        favoritesPagination: {
          ...state.favoritesPagination,
          ...toPaginate(payload),
        },
      };
    },

    // Optimistic favorite toggle: flips `isFavorite` on the main grid
    // (`data` / `premium`) AND inserts/removes the item from the
    // `favorites` slice so the favorites tab reflects the change with
    // zero network traffic. The slice is reconciled the next time the
    // user opens the favorites tab (`onTabChange` → `fetchFavorites`).
    querySetFavorite(state, { payload }) {
      const { id, isFavorite } = payload;
      const next = !isFavorite;
      const updateItem = (item) => (item.id === id ? { ...item, isFavorite: next } : item);

      let favorites = state.favorites;
      if (next) {
        const target =
          state.data.find((it) => it.id === id) || state.premium.find((it) => it.id === id);
        if (target && !favorites.some((f) => f.id === id)) {
          favorites = [...favorites, { ...target, isFavorite: true }];
        }
      } else {
        favorites = favorites.filter((f) => f.id !== id);
      }

      return {
        ...state,
        data: state.data.map(updateItem),
        premium: state.premium.map(updateItem),
        favorites,
      };
    },

    queryDeleteItem(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id),
        premium: state.premium.filter((item) => item.id !== payload.id),
      };
    },

    markFavoritesChanged(state) {
      return { ...state, favoritesChanged: true };
    },

    clearFavoritesChanged(state) {
      return { ...state, favoritesChanged: false };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        premium: state.premium.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    queryEntityGroup(state, { payload }) {
      return {
        ...state,
        viewGroupsExecutedCommand: payload || false,
      };
    },

    entityById(state, { payload }) {
      return { ...state, selectedItem: payload };
    },

    querySaveEntity(state, { payload }) {
      return {
        ...state,
        executedCommand: DynamicEntityOperation.Add,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    orderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    hideForms(state) {
      return {
        ...state,
        selectedItem: null,
      };
    },

    queryCleanup() {
      return initialState;
    },
  },
};

export default Model;
